import { DEFAULT_GAME_MODE, getGameMode, getModePhase, type GameModeId } from '../content/modes';
import { getCharacterAtStage, type CharacterId } from '../content/characters';
import type { SeededRandom } from './random';

export type ResonancePhase = 1 | 2 | 3 | 4 | 5;

export class DropBag {
  private items: CharacterId[] = [];
  private index = 0;

  public constructor(
    private phase: ResonancePhase,
    private readonly random: SeededRandom,
    private readonly mode: GameModeId = DEFAULT_GAME_MODE,
  ) {
    this.refill();
  }

  public draw(): CharacterId {
    if (this.index >= this.items.length) this.refill();
    const item = this.items[this.index];
    if (!item) throw new Error('Drop bag unexpectedly empty');
    this.index += 1;
    return item;
  }

  public switchPhase(phase: ResonancePhase): void {
    this.phase = phase;
    this.refill();
  }

  /** Reads ahead without changing the bag, phase, or its seeded RNG. */
  public peek(count: number): readonly CharacterId[] {
    if (!Number.isInteger(count) || count < 0) throw new RangeError('count must be a non-negative integer');
    const items = this.items;
    const index = this.index;
    const phase = this.phase;
    const random = this.random.snapshot();
    try {
      return Array.from({ length: count }, () => this.draw());
    } finally {
      this.items = items;
      this.index = index;
      this.phase = phase;
      this.random.restore(random);
    }
  }

  public remaining(): readonly CharacterId[] {
    return this.items.slice(this.index);
  }

  public candidateIds(): readonly CharacterId[] {
    const { firstStage } = getModePhase(this.mode, this.phase);
    return getGameMode(this.mode).dropCounts.map((_, offset) => getCharacterAtStage(firstStage + offset).id);
  }

  private refill(): void {
    const { firstStage } = getModePhase(this.mode, this.phase);
    const items: CharacterId[] = [];
    getGameMode(this.mode).dropCounts.forEach((count, offset) => {
      const id = getCharacterAtStage(firstStage + offset).id;
      for (let copy = 0; copy < count; copy += 1) items.push(id);
    });
    for (let index = items.length - 1; index > 0; index -= 1) {
      const target = this.random.nextInt(index + 1);
      [items[index], items[target]] = [items[target]!, items[index]!];
    }
    this.items = items;
    this.index = 0;
  }
}
